import React, { useState, useEffect } from 'react';
import { X, Loader2, AlertCircle, Package, Layers, Download } from 'lucide-react';
import { useTheme } from '../../core/ui/ThemeContext';
import { useLanguage } from '../../core/i18n/LanguageContext';
import { formatQuantity } from 'core/utils/units';
import {
  getLagerSuppliers,
  getLagerPurchaseHistory,
  getLagerClassifications,
  downloadLagerMaterialDetailExport,
} from 'core/api/apiService';
import SuppliersBlock from './detail/SuppliersBlock';
import PurchaseHistoryBlock from './detail/PurchaseHistoryBlock';
import ClassificationBlock from './detail/ClassificationBlock';
import SchnellBanfBlock from './detail/SchnellBanfBlock';
import AtpBlock from './detail/AtpBlock';

/**
 * MaterialDetail — Inline-Detailzeile fuer das Lager-Modul (Phase 2 + Phase 3a).
 *
 * Props:
 *   data            Object|null   Ergebnis von getLagerMaterialDetail
 *   loading         boolean
 *   error           string|null
 *   colSpan         number        Anzahl Spalten der Elterntabelle
 *   onClose         () => void
 *   user            Object
 *   werk            string
 *   showSuccess     (msg) => void
 *   showError       (msg) => void
 *   onCartUpdated   () => void
 *   onNavigateToCreateBanf (prefill: Object) => void
 */
const MaterialDetail = ({
  data,
  loading,
  error,
  colSpan,
  onClose,
  user,
  werk,
  showSuccess,
  showError,
  onCartUpdated,
  onNavigateToCreateBanf,
}) => {
  const { isDarkMode } = useTheme();
  const { t } = useLanguage();

  // ---- Export-State ----
  const [exportLoading, setExportLoading] = useState(false);

  const handleDetailExport = async () => {
    if (exportLoading || !data?.material?.matnr) return;
    setExportLoading(true);
    try {
      await downloadLagerMaterialDetailExport(user, data.material.matnr, werk);
    } catch (err) {
      showError(t('lm.exportError') + ': ' + (err?.message || String(err)));
    } finally {
      setExportLoading(false);
    }
  };

  // ---- Phase-3a-States ----
  const [suppliers, setSuppliers] = useState(null);
  const [suppliersLoading, setSuppliersLoading] = useState(false);
  const [suppliersError, setSuppliersError] = useState(null);

  const [purchaseHistory, setPurchaseHistory] = useState(null);
  const [historyLoading, setHistoryLoading] = useState(false);
  const [historyError, setHistoryError] = useState(null);

  const [classifications, setClassifications] = useState(null);
  const [classLoading, setClassLoading] = useState(false);
  const [classError, setClassError] = useState(null);

  // Paralleles Laden aller Phase-3a-Daten sobald Stammdaten verfuegbar
  useEffect(() => {
    if (!data?.material?.matnr) return;
    const matnr = data.material.matnr;
    let cancelled = false;

    setSuppliersLoading(true); setSuppliersError(null);
    getLagerSuppliers(user, matnr, werk)
      .then((res) => { if (!cancelled) setSuppliers(Array.isArray(res) ? res : []); })
      .catch((err) => { if (!cancelled) setSuppliersError(err?.message || String(err)); })
      .finally(() => { if (!cancelled) setSuppliersLoading(false); });

    setHistoryLoading(true); setHistoryError(null);
    getLagerPurchaseHistory(user, matnr, werk, 5)
      .then((res) => { if (!cancelled) setPurchaseHistory(Array.isArray(res) ? res : []); })
      .catch((err) => { if (!cancelled) setHistoryError(err?.message || String(err)); })
      .finally(() => { if (!cancelled) setHistoryLoading(false); });

    setClassLoading(true); setClassError(null);
    getLagerClassifications(user, matnr, werk)
      .then((res) => { if (!cancelled) setClassifications(Array.isArray(res) ? res : []); })
      .catch((err) => { if (!cancelled) setClassError(err?.message || String(err)); })
      .finally(() => { if (!cancelled) setClassLoading(false); });

    return () => { cancelled = true; };
  }, [data?.material?.matnr, werk]); // eslint-disable-line react-hooks/exhaustive-deps

  // ---- Styles ----
  const cellMuted = isDarkMode ? 'text-white/45' : 'text-slate-400';
  const cellVal   = isDarkMode ? 'text-white/85' : 'text-slate-800';
  const sectionTitle = `text-xs font-semibold uppercase tracking-wide mb-3 ${isDarkMode ? 'text-white/50' : 'text-slate-400'}`;
  const labelCls = `text-xs ${cellMuted}`;
  const valueCls = `text-sm font-medium ${cellVal}`;
  const dividerCls = `border-t ${isDarkMode ? 'border-white/10' : 'border-slate-100'}`;

  const formatPrice = (price, currency) => {
    if (price == null || price === '') return '—';
    const num = parseFloat(price);
    if (Number.isNaN(num)) return '—';
    const fmt = new Intl.NumberFormat('de-DE', { minimumFractionDigits: 2, maximumFractionDigits: 2 }).format(num);
    return currency ? `${fmt} ${currency}` : fmt;
  };

  // ---- Inhalt ----
  const renderContent = () => {
    if (loading) {
      return (
        <div className="flex items-center justify-center py-10">
          <Loader2 className={`w-5 h-5 animate-spin mr-2 ${isDarkMode ? 'text-brand' : 'text-brand'}`} />
          <span className={`text-sm ${isDarkMode ? 'text-white/50' : 'text-slate-500'}`}>
            {t('lm.loadingDetail')}
          </span>
        </div>
      );
    }

    if (error) {
      return (
        <div className="flex items-center justify-center py-10 gap-2">
          <AlertCircle className={`w-5 h-5 flex-shrink-0 ${isDarkMode ? 'text-red-400' : 'text-red-500'}`} />
          <span className={`text-sm ${isDarkMode ? 'text-white/60' : 'text-slate-600'}`}>
            {t('lm.errorDetail')}: {error}
          </span>
        </div>
      );
    }

    if (!data) return null;

    const mat = data.material ?? {};
    const stock = Array.isArray(data.stock) ? data.stock : [];

    // Bestand nach Topf gruppieren (eigen | Konsignation | beim Lieferanten) — analog
    // BlockAvailability im Produktdetail. Konsignation zählt wie verfügbarer Bestand,
    // Beistellbestand beim Lieferanten (MSLB) wird separat/rein informativ gezeigt.
    const STOCK_TYPE_ORDER = ['own', 'vendor_consignment', 'at_vendor'];
    const STOCK_TYPE_LABEL = {
      own: 'lm.detailStockOwn',
      vendor_consignment: 'lm.detailStockVendorConsignment',
      at_vendor: 'lm.detailStockAtVendor',
    };
    const stockGroups = stock.reduce((acc, s) => {
      const ty = s.stock_type || 'own';
      (acc[ty] = acc[ty] || []).push(s);
      return acc;
    }, {});

    return (
      <div className="space-y-6">

        {/* ---- Zeile 1: Stammdaten | Bestandssituation | Schnell-BANF ---- */}
        <div className="grid grid-cols-1 xl:grid-cols-3 gap-6">

          {/* Block 1: Stammdaten */}
          <div>
            <div className="flex items-center gap-2 mb-3">
              <Package className={`w-4 h-4 ${isDarkMode ? 'text-brand/70' : 'text-brand'}`} />
              <p className={sectionTitle}>{t('lm.detailStammdaten')}</p>
            </div>
            <div className="space-y-2.5">
              <div>
                <p className={labelCls}>{t('lm.colMatnr')} / {t('lm.colMaktx')}</p>
                <p className={`text-sm font-semibold font-mono ${cellVal}`}>{mat.matnr ?? '—'}</p>
                <p className={`text-sm ${cellVal}`}>{mat.maktx ?? '—'}</p>
              </div>
              {mat.bismt && (
                <div>
                  <p className={labelCls}>{t('lm.detailBismt')}</p>
                  <p className={`${valueCls} font-mono text-xs`}>{mat.bismt}</p>
                </div>
              )}
              <div>
                <p className={labelCls}>{t('lm.detailMaterialart')}</p>
                <p className={valueCls}>{mat.mtart ?? '—'}</p>
              </div>
              <div>
                <p className={labelCls}>{t('lm.detailWarengruppe')}</p>
                <p className={valueCls}>{mat.wgbez ?? mat.matkl ?? '—'}</p>
              </div>
              <div>
                <p className={labelCls}>{t('lm.detailMeins')}</p>
                <p className={`${valueCls} font-mono`}>{mat.meins ?? '—'}</p>
              </div>
              <div>
                <p className={labelCls}>{t('lm.detailBeskz')}</p>
                <p className={valueCls}>{mat.beskz_text ? `${mat.beskz_text} (${mat.beskz})` : (mat.beskz ?? '—')}</p>
              </div>
              <div>
                <p className={labelCls}>{t('lm.detailDispo')}</p>
                <p className={valueCls}>
                  {mat.dispomerkmal_text ? `${mat.dispomerkmal_text} (${mat.dispomerkmal})` : (mat.dispomerkmal ?? '—')}
                </p>
                {mat.dispo_active && (
                  <p className={`text-xs mt-0.5 ${isDarkMode ? 'text-blue-300/70' : 'text-brand/70'}`}>
                    {t('lm.dispoActive')}
                  </p>
                )}
              </div>
              {(data.letzter_preis != null || data.letzter_lieferant) && (
                <div className={`pt-2 mt-1 border-t ${dividerCls}`}>
                  {data.letzter_preis != null && (
                    <div className="mb-1.5">
                      <p className={labelCls}>{t('lm.detailLetzterPreis')}</p>
                      <p className={valueCls}>{formatPrice(data.letzter_preis, data.letzter_preis_waehrung)}</p>
                    </div>
                  )}
                  {data.letzter_lieferant && (
                    <div>
                      <p className={labelCls}>{t('lm.detailLetzterLieferant')}</p>
                      <p className={valueCls}>{data.letzter_lieferant}</p>
                    </div>
                  )}
                </div>
              )}
            </div>
          </div>

          {/* Block 2: Bestandssituation */}
          <div>
            <div className="flex items-center gap-2 mb-3">
              <Layers className={`w-4 h-4 ${isDarkMode ? 'text-brand/70' : 'text-brand'}`} />
              <p className={sectionTitle}>{t('lm.detailBestand')}</p>
            </div>
            {stock.length === 0 ? (
              <p className={`text-sm ${cellMuted}`}>{t('lm.empty')}</p>
            ) : (
              <div className="space-y-4">
                {STOCK_TYPE_ORDER.filter((ty) => stockGroups[ty]?.length).map((ty) => {
                  const rows = stockGroups[ty];
                  const isSpecial = ty !== 'own';
                  const potTotal = rows.reduce(
                    (a, s) => ({ frei: a.frei + s.frei, qp: a.qp + s.qp, gesperrt: a.gesperrt + s.gesperrt }),
                    { frei: 0, qp: 0, gesperrt: 0 }
                  );
                  return (
                    <div key={ty}>
                      <p className={`text-[11px] font-semibold uppercase tracking-wide mb-1 ${cellMuted}`}>{t(STOCK_TYPE_LABEL[ty] || 'lm.detailStockOwn')}</p>
                      <div className="overflow-x-auto">
                        <table className="w-full text-xs border-collapse">
                          <thead>
                            <tr className={`border-b ${isDarkMode ? 'border-white/10' : 'border-slate-200'}`}>
                              <th className={`text-left pb-1.5 pr-2 font-semibold ${cellMuted}`}>{isSpecial ? t('lm.detailColLieferant') : t('lm.detailColLgort')}</th>
                              <th className={`text-left pb-1.5 pr-2 font-semibold ${cellMuted}`}>{isSpecial ? '' : t('lm.detailColLgortName')}</th>
                              <th className={`text-right pb-1.5 pr-2 font-semibold ${cellMuted}`}>{t('lm.detailColFrei')}</th>
                              <th className={`text-right pb-1.5 pr-2 font-semibold ${cellMuted}`}>{t('lm.detailColQp')}</th>
                              <th className={`text-right pb-1.5 font-semibold ${cellMuted}`}>{t('lm.detailColGesperrt')}</th>
                            </tr>
                          </thead>
                          <tbody>
                            {rows.map((row, i) => (
                              <tr key={`${ty}-${row.vendor || row.lgort}-${i}`} className={`border-b ${isDarkMode ? 'border-white/5' : 'border-slate-50'}`}>
                                <td className={`py-1.5 pr-2 font-mono ${cellMuted}`}>{isSpecial ? (row.vendor || '—') : row.lgort}</td>
                                <td className={`py-1.5 pr-2 ${cellVal}`}>{isSpecial ? (row.lgort || '') : (row.lgort_name || '—')}</td>
                                <td className={`py-1.5 pr-2 text-right ${cellVal}`}>{formatQuantity(row.frei, mat.meins)}</td>
                                <td className={`py-1.5 pr-2 text-right ${cellMuted}`}>{formatQuantity(row.qp, mat.meins)}</td>
                                <td className={`py-1.5 text-right ${cellMuted}`}>{formatQuantity(row.gesperrt, mat.meins)}</td>
                              </tr>
                            ))}
                            <tr className={`border-t ${isDarkMode ? 'border-white/20' : 'border-slate-300'}`}>
                              <td colSpan={2} className={`py-1.5 pr-2 font-semibold text-xs ${cellVal}`}>{t('lm.detailGesamt')}</td>
                              <td className={`py-1.5 pr-2 text-right font-semibold ${cellVal}`}>{formatQuantity(potTotal.frei, mat.meins)}</td>
                              <td className={`py-1.5 pr-2 text-right font-semibold ${cellMuted}`}>{formatQuantity(potTotal.qp, mat.meins)}</td>
                              <td className={`py-1.5 text-right font-semibold ${cellMuted}`}>{formatQuantity(potTotal.gesperrt, mat.meins)}</td>
                            </tr>
                          </tbody>
                        </table>
                      </div>
                    </div>
                  );
                })}
                {/* Verfügbar gesamt = eigener freier Bestand + Konsignation (MKOL); MSLB zählt nicht */}
                {data.verfuegbar_gesamt != null && (
                  <div className={`flex items-center justify-between px-2.5 py-1.5 rounded-lg ${isDarkMode ? 'bg-brand/15' : 'bg-brand/8'}`}>
                    <span className={`text-xs font-semibold ${isDarkMode ? 'text-blue-200' : 'text-brand'}`}>{t('lm.detailVerfuegbarGesamt')}</span>
                    <span className={`text-sm font-bold ${isDarkMode ? 'text-blue-100' : 'text-brand'}`}>{formatQuantity(data.verfuegbar_gesamt, mat.meins)} {mat.meins || ''}</span>
                  </div>
                )}
              </div>
            )}
            {/* Meldebestand + Sicherheitsbestand */}
            <div className={`mt-4 pt-3 border-t ${dividerCls} flex gap-6`}>
              <div>
                <p className={labelCls}>{t('lm.detailMeldebestand')}</p>
                <p className={`text-sm ${cellVal}`}>
                  {data.meldebestand != null ? formatQuantity(data.meldebestand, mat.meins) + ' ' + (mat.meins || '') : '—'}
                </p>
              </div>
              <div>
                <p className={labelCls}>{t('lm.detailSicherheitsbestand')}</p>
                <p className={`text-sm ${cellVal}`}>
                  {data.sicherheitsbestand != null ? formatQuantity(data.sicherheitsbestand, mat.meins) + ' ' + (mat.meins || '') : '—'}
                </p>
              </div>
            </div>
          </div>

          {/* Block 3: Schnell-BANF */}
          <SchnellBanfBlock
            data={data}
            werk={werk}
            user={user}
            suppliers={suppliers}
            suppliersLoading={suppliersLoading}
            showSuccess={showSuccess}
            showError={showError}
            onCartUpdated={onCartUpdated}
            onNavigateToCreateBanf={onNavigateToCreateBanf}
          />
        </div>

        {/* ---- Zeile 2: ATP / Verfuegbarkeit (MD04-Stil) ---- */}
        <div className={`pt-5 border-t ${dividerCls}`}>
          <AtpBlock
            matnr={mat.matnr}
            werk={werk}
            user={user}
            meins={mat.meins}
          />
        </div>

        {/* ---- Zeile 3: Lieferanten & Konditionen (volle Breite) ---- */}
        <div className={`pt-5 border-t ${dividerCls}`}>
          <SuppliersBlock
            suppliers={suppliers}
            loading={suppliersLoading}
            error={suppliersError}
            meins={mat.meins}
          />
        </div>

        {/* ---- Zeile 4: Bestellhistorie | Klassifizierung ---- */}
        <div className={`pt-5 border-t ${dividerCls} grid grid-cols-1 xl:grid-cols-2 gap-6`}>
          <PurchaseHistoryBlock
            history={purchaseHistory}
            loading={historyLoading}
            error={historyError}
            meins={mat.meins}
          />
          <ClassificationBlock
            classifications={classifications}
            loading={classLoading}
            error={classError}
          />
        </div>

      </div>
    );
  };

  return (
    <tr>
      <td
        colSpan={colSpan}
        className={`px-4 sm:px-6 lg:px-8 py-5 ${isDarkMode
          ? 'bg-gray-900/70 border-y border-brand/20'
          : 'bg-slate-50/80 border-y border-brand/10'
        }`}
      >
        <div className="relative">
          {/* Detail-Export + Schliessen */}
          <div className="absolute right-0 top-0 flex items-center gap-1">
            {data?.material?.matnr && (
              <button
                type="button"
                onClick={handleDetailExport}
                disabled={exportLoading}
                title={exportLoading ? t('lm.exportLoading') : t('lm.exportDetailXlsx')}
                className={`inline-flex items-center gap-1 px-2 py-1 rounded-lg text-xs font-medium border transition-all ${
                  exportLoading
                    ? isDarkMode
                      ? 'bg-white/5 border-white/10 text-white/30 cursor-not-allowed'
                      : 'bg-slate-100 border-slate-200 text-slate-400 cursor-not-allowed'
                    : isDarkMode
                      ? 'bg-brand/15 border-brand/30 text-blue-200 hover:bg-brand/25'
                      : 'bg-brand/8 border-brand/30 text-brand hover:bg-brand/15'
                }`}
              >
                {exportLoading
                  ? <Loader2 className="w-3.5 h-3.5 animate-spin" />
                  : <Download className="w-3.5 h-3.5" />
                }
                <span className="hidden sm:inline">
                  {exportLoading ? t('lm.exportLoading') : t('lm.exportDetailXlsx')}
                </span>
              </button>
            )}
            <button
              type="button"
              onClick={onClose}
              aria-label={t('lm.close')}
              className={`p-1.5 rounded-lg transition-colors ${isDarkMode
                ? 'text-white/40 hover:text-white/70 hover:bg-white/8'
                : 'text-slate-400 hover:text-slate-600 hover:bg-slate-200'
              }`}
            >
              <X className="w-4 h-4" />
            </button>
          </div>
          {renderContent()}
        </div>
      </td>
    </tr>
  );
};

export default MaterialDetail;
